import React, { useMemo, useState } from 'react'
import { ScrollView, Card, Separator, Spinner, Button, Icon, YStack, H1, H2 } from '@red-ui/components'
import { getString } from '@vrw/data'
import { Header as FlightDataHeader } from '../FlightDataCard/Header'
import { SeatTypeInfo } from '../FlightDataCard/SeatTypeInfo'
import { PointsSummary } from './PointsSummary'
import { TextButton } from '../TextButton'
import { getSearchSummary, SummaryFare } from '../../api/searchSummary'
import { useSearchSummaryTexts } from '../../hooks/useSearchSummaryTexts'
import { useQuery } from '@tanstack/react-query'
import { Alert } from '../../components/Alert'
import { getTrackingId } from '../../helpers/getTrackingId'
import { VAA_RESULTS_URL } from '@/config'
import { FarePriceCurrency } from '../../api/searchFlights'
import { useFlightsSearchParams } from '../../hooks/useFlightsSearchParams'
import { generateSearchQueryString } from '../../helpers/searchQueryParams'

export type Props = {
  basketId?: string
  onClose: () => void
  onEditSearch: () => void
  onContinue: () => void
}

export const FlightSummary = ({ basketId, onClose, onContinue, onEditSearch }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const { passengers, title, subTitle } = useSearchSummaryTexts()
  const { searchParams } = useFlightsSearchParams()
  const offerId = searchParams?.offerId ?? null
  const sliceId = searchParams?.sliceId ?? null
  const { data, isLoading, error } = useSearchSummary(basketId, offerId)

  const searchQuery = useMemo(() => {
    if (!searchParams) return ''

    const {
      form: { departure, destination, date, ...passengers },
    } = searchParams

    const outboundQuery = generateSearchQueryString({
      departure,
      destination,
      date: { from: date.from },
      ...passengers,
    })

    if (!date.to) return `?${outboundQuery}`

    const returnQuery = generateSearchQueryString({
      departure: destination,
      destination: departure,
      date: { from: date.to },
    })

    return `?${outboundQuery}&${returnQuery}`
  }, [searchParams])

  const flights = data?.flights ?? []
  const isRoundTrip = flights.length > 1 && !!sliceId
  const headerSubtitle = getString(
    `redSkySearch.results.sidesheet.summary.${flights.length > 1 ? 'roundtrip' : 'oneway'}.passengers`
  ).replace('{{passengersText}}', passengers)

  const fares = data?.fares ?? []
  const flightFiatPrices = groupFarePricesByCurrency(fares)
  const flightPointsPrice = calculateFlightPointsPrice(fares)

  const handleEditSearch = () => {
    onClose()
    onEditSearch()
  }

  const handleContinue = () => {
    onContinue()
    window.open(`${VAA_RESULTS_URL}${searchQuery}`)
  }

  return (
    <ScrollView contentContainerStyle={{ flexGrow: 1, justifyContent: 'space-between', gap: '$3' }}>
      <YStack
        paddingHorizontal="$2"
        paddingTop="$2"
        gap="$3"
        $gtMobile={{ paddingTop: '$3', paddingHorizontal: '$3' }}
        $gtTabletLandscape={{ paddingTop: '$5' }}>
        {isLoading ? (
          <Spinner color="$red6" size="large" />
        ) : (
          <>
            <YStack gap="$1">
              <H1 fontSize="$8" $mobile={{ lineHeight: '$7' }} $gtTabletLandscape={{ fontSize: '$9', lineHeight: '$5' }} fontWeight="600">
                {title}
              </H1>
              <H2 fontSize="$4" fontWeight="unset">
                {subTitle}
              </H2>
            </YStack>
            <YStack gap="$4">
              {!!error && (
                <Alert
                  boxStyling={{ backgroundColor: '$pink10' }}
                  title={getString('redSkySearch.results.sidesheet.summary.error.title')}
                  body={getString('redSkySearch.results.sidesheet.summary.error.message')}
                  icon={<Icon.AlertCircle color="$pink10" fill="#B3261E" alignSelf="center" minWidth="$3" />}
                />
              )}
              {flights.map((flight, index) => (
                <YStack key={`${flight.arrival}_${flight.departure}_${flight.origin.code}_${flight.destination.code}`}>
                  <Card padding="$3" borderWidth={1} borderColor="$black2">
                    <FlightDataHeader data={flight} inSideSheet subtitle={headerSubtitle} />
                    <YStack marginVertical="$2" $gtTabletPortrait={{ marginVertical: '$3' }}>
                      <SeatTypeInfo id={isRoundTrip && index === 0 ? sliceId : offerId} expanded={isExpanded} onExpand={setIsExpanded} />
                    </YStack>
                    <Separator />
                    <YStack alignItems="center" marginTop="$3">
                      <TextButton $gtMobile={{ fontSize: '$5' }} onPress={onClose}>
                        {getString('redSkySearch.results.sidesheet.summary.change')}
                      </TextButton>
                    </YStack>
                  </Card>
                </YStack>
              ))}
            </YStack>
            {!error && <PointsSummary fiatPrices={flightFiatPrices} points={flightPointsPrice} />}
          </>
        )}
      </YStack>
      <YStack paddingHorizontal="$2" paddingBottom="$3" $gtMobile={{ paddingHorizontal: '$3' }}>
        <YStack gap="$2" flexDirection="column" $gtMobile={{ flexDirection: 'row' }}>
          <Button
            disabled={isLoading || !!error}
            disabledStyle={{ backgroundColor: '$pink10', borderColor: '$pink10', cursor: 'auto' }}
            id={getTrackingId('flight-summary', getString('actions.continue'))}
            flex={1}
            height="$6"
            icon={<Icon.ArrowUpRightFromSquare />}
            onPress={handleContinue}>
            {getString('actions.continue')}
          </Button>
          <Button flex={1} height="$6" onPress={handleEditSearch} theme="secondary">
            {getString('redSkySearch.results.sidesheet.summary.editSearch')}
          </Button>
        </YStack>
      </YStack>
    </ScrollView>
  )
}

function calculateFlightPointsPrice(fares: SummaryFare[]) {
  return fares.reduce((acc: number, fare) => {
    return acc + Number(fare.price.awardPoints) * fare.quantity
  }, 0)
}

function groupFarePricesByCurrency(fares: SummaryFare[]) {
  return fares.reduce(
    (acc, fare) => {
      const currency = fare.price.currency
      acc[currency] ??= 0
      acc[currency] += fare.price.amountIncludingTax * fare.quantity
      return acc
    },
    {} as Record<FarePriceCurrency, number>
  )
}

function useSearchSummary(basketId?: string, offerId?: string | null) {
  return useQuery({
    queryKey: ['redsky-search-summary', basketId, offerId],
    enabled: !!basketId && !!offerId,
    queryFn: () => {
      if (!basketId || !offerId) return null
      return getSearchSummary(basketId, offerId)
    },
  })
}
